'use client';

import { useEffect, useRef, useState } from 'react';
import { IMG } from '../../imgPrefix';
import { useRebrand } from '../../_components/TenantBrandProvider';

const SLIDES = [
  {
    href: `${IMG}/sports`,
    src: `${IMG}/image/hotel-landing.png`,
    alt: 'Book It Please',
  },
  {
    href: `${IMG}/form/form-9-5tptwv`,
    src: `${IMG}/image/BIP-HP-Experience-your-trip-to-London.png`,
    alt: 'Experience your trip to London',
  },
];

// Leading/trailing clones enable a one-directional infinite loop: the strip
// always animates the same way (left on next, right on prev); once it lands
// on a clone we snap back to the matching real slide with transitions off.
const EXTENDED = [SLIDES[SLIDES.length - 1], ...SLIDES, SLIDES[0]];
const FIRST_REAL = 1;
const LAST_REAL = SLIDES.length;

export default function ExperienceCarousel() {
  const { rb } = useRebrand();
  const [index, setIndex] = useState(FIRST_REAL);
  const [animate, setAnimate] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => i + 1), 4000);
    return () => clearInterval(id);
  }, [tick]);

  const restartAutoplay = () => setTick((t) => t + 1);

  const next = () => {
    setAnimate(true);
    setIndex((i) => i + 1);
    restartAutoplay();
  };
  const prev = () => {
    setAnimate(true);
    setIndex((i) => i - 1);
    restartAutoplay();
  };

  const handleTransitionEnd = () => {
    if (index > LAST_REAL) {
      setAnimate(false);
      setIndex(FIRST_REAL);
    } else if (index < FIRST_REAL) {
      setAnimate(false);
      setIndex(LAST_REAL);
    }
  };

  // Re-enable the transition on the next frame after a silent snap-back.
  const wasAnimating = useRef(animate);
  useEffect(() => {
    if (!animate && wasAnimating.current) {
      const id = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(id);
    }
    wasAnimating.current = animate;
    return undefined;
  }, [animate]);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-yellow-light aspect-[4000/915]">
      <div
        className={`flex h-full ${animate ? 'transition-transform duration-700 ease-in-out' : ''}`}
        style={{ transform: `translateX(-${index * 100}%)` }}
        onTransitionEnd={handleTransitionEnd}
      >
        {EXTENDED.map((slide, i) => (
          <a
            key={`${slide.src}-${i}`}
            href={slide.href}
            target="_blank"
            rel="noopener noreferrer"
            className="block h-full w-full flex-shrink-0"
          >
            {/* Images already carry their own decorative corner cutouts — contain
                (not cover) so they're never cropped; the bg-yellow-light behind
                them matches the page background, so any letterboxing is invisible. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={slide.src} draggable={false} alt={rb(slide.alt)} className="h-full w-full object-contain" />
          </a>
        ))}
      </div>
      <button type="button" onClick={prev} className="carousel-control-prev" aria-label="Previous">
        <i aria-hidden="true" className="fas fa-chevron-left" />
        <span className="sr-only">Previous</span>
      </button>
      <button type="button" onClick={next} className="carousel-control-next" aria-label="Next">
        <i aria-hidden="true" className="fas fa-chevron-right" />
        <span className="sr-only">Next</span>
      </button>
    </div>
  );
}
